import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Star, Users, TrendingUp, GitFork, Crown, Medal, Award } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';

const TABS = [
  { key: 'top-personas', label: 'Top Personas', icon: Crown },
  { key: 'top-playbooks', label: 'Top Playbooks', icon: Trophy },
  { key: 'rising', label: 'Rising', icon: TrendingUp },
];

const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32']; // gold, silver, bronze

export default function Leaderboard() {
  const [activeTab, setActiveTab] = useState('top-personas');
  const [personas, setPersonas] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState('rating'); // rating | uses

  useEffect(() => {
    setLoading(true);
    Promise.all([
      API.get('/api/personas').then(r => setPersonas(r.data.personas || r.data || [])).catch(() => setPersonas([])),
      API.get('/api/playbooks').then(r => setPlaybooks(r.data.playbooks || [])).catch(() => setPlaybooks([])),
    ]).finally(() => setLoading(false));
  }, []);

  const ranked = useMemo(() => {
    let items = [];
    if (activeTab === 'top-personas') items = [...personas];
    else if (activeTab === 'top-playbooks') items = [...playbooks];
    else items = [...personas, ...playbooks].map(i => ({ ...i, _type: personas.includes(i) ? 'persona' : 'playbook' }));

    if (activeTab === 'rising') {
      items.sort((a, b) => (b.trending_score || b.use_count || 0) - (a.trending_score || a.use_count || 0));
    } else if (sortMode === 'rating') {
      items.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
    } else {
      items.sort((a, b) => (b.use_count || 0) - (a.use_count || 0));
    }

    return items.slice(0, 50);
  }, [activeTab, personas, playbooks, sortMode]);

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-8">
          <Trophy className="w-10 h-10 text-[#FFD700] mx-auto mb-2" />
          <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed']">Leaderboard</h1>
          <p className="text-sm text-[#A0A0A0] mt-1">Top-rated and most-used community content</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#2A2A2A] overflow-x-auto" data-testid="leaderboard-tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 md:px-4 py-2.5 min-h-[44px] text-xs md:text-sm font-medium font-['Barlow_Condensed'] whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-[#FF4D00] text-[#FF4D00]'
                    : 'border-transparent text-[#A0A0A0] hover:text-[#F5F5F5]'
                }`}
                data-testid={`leaderboard-tab-${tab.key}`}
                aria-selected={activeTab === tab.key}
                role="tab"
              >
                <Icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Sort toggle for Top tabs */}
        {activeTab !== 'rising' && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setSortMode('rating')}
              className={`px-3 py-1.5 min-h-[44px] text-xs font-mono border transition-colors ${
                sortMode === 'rating' ? 'border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/10' : 'border-[#2A2A2A] text-[#A0A0A0]'
              }`}
              aria-pressed={sortMode === 'rating'}
              aria-label="Sort by rating"
            >
              <Star className="w-3 h-3 inline mr-1 -mt-0.5" /> By Rating
            </button>
            <button
              onClick={() => setSortMode('uses')}
              className={`px-3 py-1.5 min-h-[44px] text-xs font-mono border transition-colors ${
                sortMode === 'uses' ? 'border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/10' : 'border-[#2A2A2A] text-[#A0A0A0]'
              }`}
              aria-pressed={sortMode === 'uses'}
              aria-label="Sort by uses"
            >
              <Users className="w-3 h-3 inline mr-1 -mt-0.5" /> By Uses
            </button>
          </div>
        )}

        {/* Rankings */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : ranked.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#A0A0A0] text-sm">No rankings available yet.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="leaderboard-list">
            {ranked.map((item, i) => (
              <RankedCard key={item.slug || item.id} item={item} rank={i + 1} tab={activeTab} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RankedCard({ item, rank, tab }) {
  const isPersona = tab === 'top-personas' || item._type === 'persona';
  const href = isPersona ? `/voices` : `/playbooks/${item.slug}`;
  const rankColor = rank <= 3 ? RANK_COLORS[rank - 1] : '#2A2A2A';

  const RankIcon = rank === 1 ? Crown : rank === 2 ? Medal : rank === 3 ? Award : null;

  return (
    <Link
      to={href}
      className="flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/30 transition-colors"
      data-testid={`leaderboard-rank-${rank}`}
    >
      {/* Rank */}
      <div
        className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center shrink-0 border-2 font-bold font-['Barlow_Condensed'] text-sm md:text-base"
        style={{ borderColor: rankColor, color: rankColor }}
      >
        {RankIcon ? <RankIcon className="w-4 h-4 md:w-5 md:h-5" /> : rank}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] truncate">{item.name}</h3>
          {tab === 'rising' && (
            <span className="text-[9px] font-mono px-1 py-0.5 bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/30 shrink-0">
              {item._type === 'persona' ? 'PERSONA' : 'PLAYBOOK'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {item.author_name && <span className="text-[10px] font-mono text-[#555]">by {item.author_name}</span>}
          {(item.domain || item.category) && (
            <span className="text-[10px] font-mono text-[#555]">{item.domain || item.category}</span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 md:gap-4 shrink-0 text-xs font-mono">
        {item.avg_rating > 0 && (
          <span className="flex items-center gap-1 text-[#F59E0B]">
            <Star className="w-3.5 h-3.5 fill-current" /> {item.avg_rating?.toFixed(1)}
          </span>
        )}
        {item.use_count > 0 && (
          <span className="flex items-center gap-1 text-[#A0A0A0]">
            <Users className="w-3.5 h-3.5" /> {item.use_count.toLocaleString()}
          </span>
        )}
        {item.fork_count > 0 && (
          <span className="hidden md:flex items-center gap-1 text-[#555]">
            <GitFork className="w-3.5 h-3.5" /> {item.fork_count}
          </span>
        )}
        {tab === 'rising' && item.trending_score > 0 && (
          <span className="flex items-center gap-1 text-[#22C55E]">
            <TrendingUp className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
    </Link>
  );
}
